/* global BigInt */
import { useState, useCallback } from 'react';
import { useAccount, useWriteContract, useReadContract, useSwitchChain, useWaitForTransactionReceipt, useChainId, usePublicClient, useWalletClient } from 'wagmi';
import { parseUnits, formatUnits } from 'viem';
import { ethers } from 'ethers';
import { CHAIN_IDS_TO_USDC_ADDRESSES, CHAIN_IDS_TO_TOKEN_MESSENGER, CHAIN_IDS_TO_MESSAGE_TRANSMITTER, DESTINATION_DOMAINS } from '../config/cctp/chains';
import { TOKEN_MESSENGER_ABI, MESSAGE_TRANSMITTER_ABI, ERC20_ABI, FEE_HOOK_ABI } from '../config/cctp/contracts';
import { TRANSFER_STEPS, USDC_DECIMALS } from '../config/cctp/constants';
import { fetchAttestation, addressToBytes32 } from '../utils/cctp/attestation';
import { getFeeHookAddress, isFeeHookEnabled } from '../config/cctp/feeHook';
import BridgeApiService from '../services/bridgeApi';

export const useCCTPTransfer = () => {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { switchChainAsync, chains, error: switchError, isError: isSwitchError } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();

  const [transferStep, setTransferStep] = useState(TRANSFER_STEPS.IDLE);
  const [error, setError] = useState(null);
  const [txHashes, setTxHashes] = useState({ burn: null, mint: null });
  const [attestation, setAttestation] = useState(null);
  const [pendingTxHash, setPendingTxHash] = useState(null);

  // Use wagmi's transaction receipt hook
  const { data: txReceipt, isLoading: isWaitingForTx } = useWaitForTransactionReceipt({
    hash: pendingTxHash,
    enabled: !!pendingTxHash,
  });

  // Add a helper to check actual chain more reliably
  const getActualChainId = useCallback(async () => {
    try {
      // First, try to get from wallet client which is most accurate
      if (walletClient?.chain?.id) {
        console.log('Chain from walletClient:', walletClient.chain.id);
        return walletClient.chain.id;
      }

      // Try direct RPC call through walletClient
      if (walletClient?.request) {
        try {
          const chainHex = await walletClient.request({ method: 'eth_chainId' });
          const chainDecimal = parseInt(chainHex, 16);
          console.log('Chain from walletClient RPC:', chainDecimal);
          return chainDecimal;
        } catch (e) {
          console.log('walletClient RPC failed:', e);
        }
      }

      // Try getting from public client
      try {
        const pubChainId = await publicClient.getChainId();
        console.log('Chain from publicClient:', pubChainId);
        return pubChainId;
      } catch (e) {
        console.log('publicClient.getChainId failed:', e);
      }

      // Fallback to chainId hook (might be stale)
      console.log('Falling back to hook chainId:', chainId);
      return chainId;
    } catch (error) {
      console.error('Error getting chain ID:', error);
      return chainId;
    }
  }, [walletClient, publicClient, chainId]);

  const waitForTransactionWithPolling = useCallback(
    async (hash, maxAttempts = 60) => {
      console.log(`Starting to poll for transaction: ${hash}`);

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          // Try to get the transaction receipt
          const receipt = await publicClient.getTransactionReceipt({ hash });

          if (receipt) {
            console.log(`Transaction receipt found after ${attempt + 1} attempts:`, receipt);

            // Check if transaction was successful
            if (receipt.status === 'success') {
              console.log('Transaction confirmed successfully!');
              return receipt;
            } else {
              throw new Error('Transaction failed on chain');
            }
          }
        } catch (error) {
          // If it's not a "not found" error, log it
          if (!error.message?.includes('not found') && !error.message?.includes('null')) {
            console.log(`Attempt ${attempt + 1} error:`, error.message);
          }
        }

        // Log progress every 10 attempts
        if ((attempt + 1) % 10 === 0) {
          console.log(`Still waiting for transaction... (attempt ${attempt + 1}/${maxAttempts})`);
        }

        // Wait 2 seconds before next attempt
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      throw new Error(`Transaction not confirmed after ${maxAttempts} attempts (${maxAttempts * 2} seconds)`);
    },
    [publicClient]
  );

  const transfer = useCallback(
    async (sourceChainId, destinationChainId, amount, isFastTransfer = false) => {
      if (!address) {
        setError('Please connect your wallet');
        return;
      }

      try {
        setTransferStep(TRANSFER_STEPS.APPROVING);
        setError(null);
        setTxHashes({ burn: null, mint: null });

        // Switch chain if needed
        if (chainId !== sourceChainId) {
          console.log('Switching from chain', chainId, 'to', sourceChainId);
          await switchChainAsync({ chainId: sourceChainId });

          // Wait for chain switch to complete
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Verify switch
          const newChainId = await getActualChainId();
          console.log('Chain after switch:', newChainId);
        }

        const amountInWei = parseUnits(amount.toString(), USDC_DECIMALS);
        const usdcAddress = CHAIN_IDS_TO_USDC_ADDRESSES[sourceChainId];
        const tokenMessengerAddress = CHAIN_IDS_TO_TOKEN_MESSENGER[sourceChainId];

        console.log('Approving USDC:', {
          usdcAddress,
          tokenMessenger: tokenMessengerAddress,
          amount: amountInWei.toString(),
        });

        // Approve USDC spending
        const approveTx = await writeContractAsync({
          address: usdcAddress,
          abi: ERC20_ABI,
          functionName: 'approve',
          args: [tokenMessengerAddress, amountInWei],
        });

        console.log('Approval transaction hash:', approveTx);

        // Use backend API to monitor approval transaction for better reliability
        console.log('Monitoring approval transaction via backend API...');

        let approvalReceipt;
        try {
          const monitorResponse = await BridgeApiService.monitorTransaction(approveTx, sourceChainId);

          if (monitorResponse.success && monitorResponse.data) {
            if (monitorResponse.data.status === 'confirmed') {
              if (monitorResponse.data.success) {
                console.log('Approval confirmed via API:', monitorResponse.data);
                approvalReceipt = monitorResponse.data.receipt;
              } else {
                throw new Error('Approval transaction failed on-chain');
              }
            } else if (monitorResponse.data.status === 'pending') {
              console.warn('Approval still pending after timeout, falling back to polling...');
              // Fallback to polling if timeout
              approvalReceipt = await waitForTransactionWithPolling(approveTx);
            } else if (monitorResponse.data.status === 'not_found') {
              throw new Error('Approval transaction not found on chain');
            }
          } else {
            throw new Error('Invalid response from monitoring API');
          }
        } catch (monitorError) {
          console.error('API monitoring failed, using fallback:', monitorError);
          // Fallback to original polling method if API fails
          try {
            approvalReceipt = await waitForTransactionWithPolling(approveTx);
            console.log('Approval confirmed via fallback polling:', approvalReceipt);
          } catch (waitError) {
            console.error('Fallback polling also failed:', waitError);
            throw new Error(`Approval transaction failed: ${waitError.message}`);
          }
        }

        console.log('Approval completed successfully');

        setTransferStep(TRANSFER_STEPS.BURNING);

        const destinationDomain = DESTINATION_DOMAINS[destinationChainId];

        // Check if fee hook is enabled on destination chain
        const feeHookAddress = getFeeHookAddress(destinationChainId);
        const useFeeHook = isFeeHookEnabled(destinationChainId);

        // Set up mint recipient and destination caller based on fee hook
        let mintRecipient;
        let destinationCaller;
        let hookData = null;

        if (useFeeHook && feeHookAddress) {
          // If fee hook is enabled, mint to fee hook contract
          mintRecipient = addressToBytes32(feeHookAddress);
          // Set destination caller to fee hook so only it can call receiveMessage
          destinationCaller = addressToBytes32(feeHookAddress);
          // Create hook data with the actual recipient address
          hookData = ethers.utils.defaultAbiCoder.encode(['address'], [address]);
          console.log('Using fee hook:', {
            feeHookAddress,
            actualRecipient: address,
            hookData: hookData, // ← Add this to see the actual encoded data
            hookDataLength: hookData.length,
          });
        } else {
          // Normal flow without fee hook
          mintRecipient = addressToBytes32(address);
          destinationCaller = '0x0000000000000000000000000000000000000000000000000000000000000000';
        }

        // Import the fee calculation
        const { calculateCCTPFees, getFinalityThreshold } = await import('../config/cctp/fees');
        const feeCalc = calculateCCTPFees(amountInWei.toString(), sourceChainId, isFastTransfer, destinationChainId);

        // For CCTP, maxFee is the fee amount, not a rate
        // IMPORTANT: Always round UP to avoid insufficient_fee errors
        // Add 1 to ensure we always round up when converting from float calculations
        let maxFee = BigInt(feeCalc.fee);
        if (maxFee > 0n) {
          // Add a small buffer (1 USDC unit) to ensure we never underpay
          maxFee = maxFee + 1n;
        }
        const minFinalityThreshold = getFinalityThreshold(feeCalc.isFastTransfer);

        console.log('Transfer configuration:', {
          isFastTransfer: feeCalc.isFastTransfer,
          maxFee: maxFee.toString(),
          minFinalityThreshold,
          feeRate: feeCalc.feeRate,
          useFeeHook,
          feeHookAddress,
        });

        const functionName = useFeeHook && hookData ? 'depositForBurnWithHook' : 'depositForBurn';
        const args =
          useFeeHook && hookData
            ? [amountInWei, destinationDomain, mintRecipient, usdcAddress, destinationCaller, maxFee, minFinalityThreshold, hookData]
            : [amountInWei, destinationDomain, mintRecipient, usdcAddress, destinationCaller, maxFee, minFinalityThreshold];

        console.log(`${functionName} parameters:`, {
          tokenMessenger: tokenMessengerAddress,
          amount: amountInWei.toString(),
          destinationDomain,
          mintRecipient,
          burnToken: usdcAddress,
          destinationCaller,
          maxFee: maxFee.toString(),
          minFinalityThreshold,
          sourceChainId,
          destinationChainId: destinationChainId,
          hookData: hookData ? '0x' + hookData.slice(2) : undefined,
        });

        // Ensure mintRecipient is properly formatted
        if (!mintRecipient.startsWith('0x')) {
          throw new Error('Invalid mintRecipient format');
        }

        const burnTx = await writeContractAsync({
          address: tokenMessengerAddress,
          abi: TOKEN_MESSENGER_ABI,
          functionName,
          args,
          chainId: sourceChainId,
        });

        console.log('Burn transaction:', burnTx);
        setTxHashes((prev) => ({ ...prev, burn: burnTx }));

        // Create transaction in database in pending state immediately after burn tx is submitted
        try {
          await BridgeApiService.createTransaction({
            userAddress: address,
            sourceChain: sourceChainId,
            destinationChain: destinationChainId,
            amount: amount,
            burnTxHash: burnTx,
            burnBlockNumber: null, // Will be updated after confirmation
            sourceDomain: DESTINATION_DOMAINS[sourceChainId],
            destinationDomain: DESTINATION_DOMAINS[destinationChainId],
            nonce: null, // Will be extracted from logs after confirmation
            isFastTransfer: isFastTransfer,
            status: 'burning', // Explicitly set as burning/pending
          });
          console.log('Transaction created in pending state');
        } catch (dbError) {
          console.error('Failed to create pending transaction in database:', dbError);
          // Continue even if database storage fails
        }

        setTransferStep(TRANSFER_STEPS.WAITING_ATTESTATION);

        // Use backend API to monitor burn transaction for better reliability
        console.log('Monitoring burn transaction via backend API...');
        let receipt;
        try {
          const monitorResponse = await BridgeApiService.monitorTransaction(burnTx, sourceChainId);

          if (monitorResponse.success && monitorResponse.data) {
            if (monitorResponse.data.status === 'confirmed') {
              if (monitorResponse.data.success) {
                console.log('Burn confirmed via API:', monitorResponse.data);
                receipt = monitorResponse.data.receipt;
              } else {
                throw new Error('Burn transaction failed on-chain');
              }
            } else if (monitorResponse.data.status === 'pending') {
              console.warn('Burn still pending after timeout, falling back to polling...');
              receipt = await waitForTransactionWithPolling(burnTx);
            } else {
              throw new Error('Burn transaction not found on chain');
            }
          } else {
            throw new Error('Invalid response from monitoring API');
          }
        } catch (monitorError) {
          console.error('API monitoring failed for burn, using fallback:', monitorError);
          // Fallback to original polling method
          try {
            receipt = await waitForTransactionWithPolling(burnTx);
          } catch (waitError) {
            console.error('Fallback polling also failed:', waitError);
            // Don't throw here - transaction is already recorded as pending
            console.error('Transaction confirmation failed but is recorded as pending');
            return; // Exit early, transaction will be handled by cronjob
          }
        }
        //console.log('Burn transaction receipt:', receipt);

        // Update transaction in database with confirmation details
        if (receipt) {
          try {
            await BridgeApiService.updateBurnTransaction(burnTx, {
              burnBlockNumber: receipt.blockNumber,
              nonce: receipt.logs?.[0]?.data, // Extract nonce from logs
              status: 'waiting_attestation',
            });
            console.log('Transaction updated with confirmation details');
          } catch (dbError) {
            console.error('Failed to update transaction in database:', dbError);
            // Continue even if database update fails
          }
        }

        // Switch to destination chain after burn completes
        //console.log(`Burn complete. Switching to destination chain (${destinationChainId})...`);

        // Get actual chain ID before attempting switch
        const actualChainBeforeSwitch = await getActualChainId();
        //console.log('Actual chain before switch:', actualChainBeforeSwitch);

        if (actualChainBeforeSwitch !== destinationChainId) {
          try {
            await switchChainAsync({ chainId: destinationChainId });

            // Give more time for the switch to complete
            await new Promise((resolve) => setTimeout(resolve, 3000));

            const actualChainAfterSwitch = await getActualChainId();
            console.log('Actual chain after switch:', actualChainAfterSwitch);

            if (actualChainAfterSwitch === destinationChainId) {
              console.log('Chain switch complete');
            } else {
              console.log('Chain switch may not have completed, but continuing...');
            }
          } catch (switchErr) {
            console.log('Early chain switch error (non-fatal):', switchErr.message);
          }
        }

        //console.log('Transaction hash:', burnTx);

        // Extract message bytes from logs for later use in receiveMessage
        const messageSentTopic = '0x8c5261668696ce22758910d05bab8f186d6eb247ceac2af2e82c7dc17669b036';
        const messageSentLog = receipt.logs.find((log) => log.topics && log.topics[0] === messageSentTopic);

        if (!messageSentLog) {
          console.error('Available logs:', receipt.logs);
          throw new Error('MessageSent event not found in transaction logs');
        }

        //console.log('MessageSent log found:', messageSentLog);
        const messageBytes = messageSentLog.data;
        //console.log('Message bytes:', messageBytes);

        // Get source domain for the API call
        const sourceDomain = DESTINATION_DOMAINS[sourceChainId];
        //console.log('Source domain for API:', sourceDomain);

        //console.log('Fetching attestation from Circle API...');
        // Pass the transaction hash (burnTx) instead of message hash
        const attestationResponse = await fetchAttestation(burnTx, sourceDomain);

        // Extract attestation and message from the response
        let attestationData;
        let messageData;

        if (attestationResponse.messages && attestationResponse.messages.length > 0) {
          const message = attestationResponse.messages[0];
          attestationData = message.attestation;
          messageData = message.message;
          //console.log('Attestation data:', attestationData);
          //console.log('Message data from API:', messageData);
        } else if (attestationResponse.attestation) {
          attestationData = attestationResponse.attestation;
          messageData = attestationResponse.message || messageBytes;
        } else {
          throw new Error('No attestation found in API response');
        }

        setAttestation(attestationData);

        //console.log('Attestation received:', attestationData);

        // Save attestation to database via backend API
        try {
          console.log('Saving attestation to database...');
          await BridgeApiService.checkAttestation(burnTx);
          // The backend will fetch and cache the attestation
          console.log('Attestation saved to database');
        } catch (dbError) {
          console.error('Failed to save attestation to database:', dbError);
          // Continue even if database save fails
        }

        // Store the message and attestation for manual minting
        const mintData = {
          message: messageData || messageBytes,
          attestation: attestationData,
          sourceChain: sourceChainId,
          destinationChain: destinationChainId,
          burnHash: burnTx,
        };

        // Save the attestation data for manual minting
        const pendingTx = {
          sourceChain: sourceChainId,
          destinationChain: destinationChainId,
          amount,
          burnHash: burnTx,
          mintHash: null,
          status: 'attestation_ready',
          mintData: mintData,
          timestamp: Date.now(),
          id: `${burnTx}_${Date.now()}`,
        };

        // Store in local storage for persistence
        const existingTxs = JSON.parse(localStorage.getItem('pendingBridgeTransactions') || '[]');
        const updated = existingTxs.filter((tx) => tx.burnHash !== burnTx);
        updated.push(pendingTx);
        localStorage.setItem('pendingBridgeTransactions', JSON.stringify(updated));

        // Also update the attestation in the database (it's already stored with status)
        // The database should already have this attestation from the API check above

        console.log(`Attestation ready! Users can now mint USDC on destination chain (${destinationChainId}) manually.`);

        // Set transfer step to indicate ready to mint
        setTransferStep(TRANSFER_STEPS.READY_TO_MINT);
      } catch (err) {
        console.error('Transfer error:', err);
        setError(err.message || 'Transfer failed');
        setTransferStep(TRANSFER_STEPS.ERROR);
      }
    },
    [address, chainId, switchChainAsync, writeContractAsync, getActualChainId, waitForTransactionWithPolling, publicClient]
  );

  const manualMint = useCallback(
    async (mintData, destinationChainId) => {
      try {
        setTransferStep(TRANSFER_STEPS.MINTING);
        setError(null);

        console.log('Manual mint initiated with data:', mintData);

        const messageTransmitterAddress = CHAIN_IDS_TO_MESSAGE_TRANSMITTER[destinationChainId];

        // Check if fee hook is enabled for this chain
        const feeHookAddr = getFeeHookAddress(destinationChainId);
        const shouldUseFeeHook = isFeeHookEnabled(destinationChainId);

        // Try mint using fee hook if enabled, otherwise use direct receiveMessage
        const mintTx =
          shouldUseFeeHook && feeHookAddr
            ? await writeContractAsync({
                address: feeHookAddr,
                abi: FEE_HOOK_ABI,
                functionName: 'receiveMessageWithFee',
                args: [mintData.message, mintData.attestation],
              })
            : await writeContractAsync({
                address: messageTransmitterAddress,
                abi: MESSAGE_TRANSMITTER_ABI,
                functionName: 'receiveMessage',
                args: [mintData.message, mintData.attestation],
              });

        console.log(`Mint transaction (${shouldUseFeeHook ? 'via fee hook' : 'direct'}):`, mintTx);

        console.log('Manual mint transaction:', mintTx);
        setTxHashes((prev) => ({ ...prev, mint: mintTx }));

        // Use backend API to monitor mint transaction
        console.log('Monitoring manual mint transaction via backend API...');
        try {
          const monitorResponse = await BridgeApiService.monitorTransaction(mintTx, destinationChainId);

          if (monitorResponse.success && monitorResponse.data) {
            if (monitorResponse.data.status === 'confirmed') {
              if (monitorResponse.data.success) {
                console.log('Manual mint confirmed via API!', monitorResponse.data);
              } else {
                throw new Error('Manual mint transaction failed on-chain');
              }
            } else if (monitorResponse.data.status === 'pending') {
              console.warn('Manual mint still pending after timeout');
              // Continue anyway since transaction was submitted
            } else {
              throw new Error('Manual mint transaction not found on chain');
            }
          }
        } catch (monitorError) {
          console.error('API monitoring failed for manual mint, using fallback:', monitorError);
          // Fallback to polling if API fails
          await waitForTransactionWithPolling(mintTx);
        }

        console.log('Manual mint successful!');
        setTransferStep(TRANSFER_STEPS.COMPLETED);

        // Update storage
        // Update mint transaction in database
        try {
          await BridgeApiService.updateMintTransaction(mintData.burnHash, mintTx);
          await BridgeApiService.completeTransaction(mintData.burnHash);
          console.log('Mint transaction updated in database');
        } catch (dbError) {
          console.error('Failed to update mint in database:', dbError);
        }

        return mintTx;
      } catch (error) {
        console.error('Manual mint failed:', error);
        setError(error.message);
        setTransferStep(TRANSFER_STEPS.ERROR);
        throw error;
      }
    },
    [writeContractAsync]
  );

  const reset = () => {
    setTransferStep(TRANSFER_STEPS.IDLE);
    setError(null);
    setTxHashes({ burn: null, mint: null });
    setAttestation(null);
    setPendingTxHash(null);
  };

  return {
    transfer,
    manualMint,
    transferStep,
    error,
    txHashes,
    attestation,
    reset,
    isWaitingForTx,
  };
};

export const useUSDCBalance = (chainId) => {
  const { address } = useAccount();

  const usdcAddress = chainId ? CHAIN_IDS_TO_USDC_ADDRESSES[chainId] : undefined;

  const {
    data: balance,
    isLoading,
    refetch,
    error,
  } = useReadContract({
    address: usdcAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: chainId, // Specify which chain to query
    enabled: !!address && !!chainId && !!usdcAddress,
  });

  if (error) {
    console.error('Error fetching balance:', error);
  }

  const formattedBalance = balance ? formatUnits(balance, USDC_DECIMALS) : '0';

  return {
    balance: formattedBalance,
    isLoading,
    refetch,
  };
};
